import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {FC, useState} from "react";
import {arrayMove} from "@dnd-kit/sortable";
import ColumnContainer from "./Board/Column";
import ReactDOM from "react-dom";
import ProposalCard from "./Board/ProposalCard";
import useCandidates from "hooks/useCandidates";
import {ICandidate} from "api/types";
import FullLoader from "components/loaders/FullLoader";
import useRecruiter from "contexts/User/useRecruiter";
import {Lock} from "lucide-react";

const COLUMNS = [
  {id: "contact", label: "Shortlist"},
  {id: "progress", label: "Interview List"},
  {id: "done", label: "Hires"},
] as const;

const CandidatesPage: FC = () => {
  const {candidates, setCandidates, setChanges, patchUpdates} = useCandidates();
  const user = useRecruiter();

  const onContactSales = () => {
    const email = "anthonybirch@akadian.com";
    const subject = "Interested in Talent Finder";
    const body = "Hi Anthony, I'm interested in Talent Finder. Can you please provide more information?";
    window.open(`mailto:${email}?subject=${subject}&body=${body}`);
  };

  const sensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 10}}));

  const [activeTask, setActiveTask] = useState<ICandidate | null>(null);

  const loading = candidates === undefined;

  // @ts-ignore
  const locked = user.isEnterprise && !user.hasTalentFinder;

  if (locked) {
    return (
      <div className="w-full h-full flex flex-col overflow-hidden bg-brand-cream">
        <div className="relative flex-1 overflow-hidden">
          {/* Blurred skeleton board */}
          <div className="absolute inset-0 flex gap-px bg-brand-border blur-sm pointer-events-none select-none">
            {COLUMNS.map((col) => (
              <div key={col.id} className="flex-1 bg-white flex flex-col">
                <div className="px-5 py-4 border-b border-brand-border bg-brand-surface">
                  <div className="h-4 bg-brand-cream-dark rounded w-1/2" />
                </div>
                <div className="p-4 flex flex-col gap-3 bg-brand-cream flex-1">
                  {Array.from({length: 3}).map((_, i) => (
                    <div key={i} className="bg-white rounded-xl p-4 flex flex-col gap-2 border border-brand-border">
                      <div className="h-3 bg-brand-cream-dark rounded w-3/4" />
                      <div className="h-3 bg-brand-cream-dark rounded w-1/2" />
                      <div className="h-3 bg-brand-cream-dark rounded w-2/3" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {/* Centered upgrade CTA */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="bg-white rounded-2xl border border-brand-border p-8 flex flex-col items-center gap-4 text-center max-w-sm w-full"
              style={{boxShadow: "0 8px 32px rgba(40,45,77,0.1)"}}
            >
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-brand-cream-dark">
                <Lock className="w-6 h-6 text-brand-navy" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-brand-navy mb-1">Upgrade Your Plan</h3>
                <p className="text-sm text-brand-muted">
                  You don't have access to Candidate Tracker. Contact us to unlock this feature.
                </p>
              </div>
              <button onClick={onContactSales} className="btn-primary">
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col overflow-hidden bg-brand-cream">
      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragOver={onDragOver}>
        <div className="flex-1 flex overflow-hidden">
          {loading ? (
            <FullLoader />
          ) : (
            COLUMNS.map((col, i) => {
              const colCandidates = candidates!.filter((e) => e.state === col.id);
              return (
                <ColumnContainer
                  key={col.id}
                  column={{id: col.id}}
                  deleteTask={deleteProposal}
                  changeProposalNote={changeProposalNote}
                  proposals={colCandidates}
                  resetChanges={resetChanges}
                  isLast={i === COLUMNS.length - 1}
                >
                  <div className="flex items-center px-5 py-4 gap-3 border-b border-brand-border bg-brand-surface flex-shrink-0">
                    <span className="text-sm font-semibold text-brand-navy grow">{col.label}</span>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-brand-cream text-brand-muted">
                      {colCandidates.length}
                    </span>
                  </div>
                </ColumnContainer>
              );
            })
          )}
        </div>
        {ReactDOM.createPortal(
          <DragOverlay className="z-10">
            {activeTask && (
              <ProposalCard
                changeProposalNote={changeProposalNote}
                proposal={activeTask}
                deleteTask={deleteProposal}
                resetChanges={() => resetChanges(activeTask.id)}
              />
            )}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </div>
  );

  function deleteProposal(id: string) {
    setCandidates((o) => o?.filter((c) => c.id !== id));
    patchUpdates(false);
  }

  function changeProposalNote(id: string, note: string) {
    setChanges((o) => ({...o, [id]: {...o[id], note}}));
    setCandidates((o) => o?.map((c) => (c.id === id ? {...c, note} : c)));
    patchUpdates(false);
  }

  function resetChanges(id: string) {
    setCandidates((o) => o?.map((c) => (c.id === id ? {...c, changes: 0} : c)));
  }

  function onDragStart(event: DragStartEvent) {
    if (event.active.data.current?.type === "Proposal") {
      setActiveTask(event.active.data.current.proposal);
    }
  }

  function onDragEnd(event: DragEndEvent) {
    patchUpdates();
    setActiveTask(null);

    const {active, over} = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const isActiveAColumn = active.data.current?.type === "Column";
    if (!isActiveAColumn) return;
  }

  function onDragOver(event: DragOverEvent) {
    const {active, over} = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const isActiveATask = active.data.current?.type === "Proposal";
    const isOverATask = over.data.current?.type === "Proposal";

    if (!isActiveATask) return;

    if (isActiveATask && isOverATask) {
      setCandidates((old) => {
        if (!old) return;
        const activeIndex = old.findIndex((t) => t.id === activeId);
        const overIndex = old.findIndex((t) => t.id === overId);

        if (old[activeIndex].state !== old[overIndex].state) {
          old[activeIndex].state = old[overIndex].state;
          setChanges((o) => ({...o, [activeId]: {...o[activeId], state: old[overIndex].state}}));
          return arrayMove(old, activeIndex, overIndex - 1);
        }

        return arrayMove(old, activeIndex, overIndex);
      });
    }

    const isOverAColumn = over.data.current?.type === "Column";

    if (isActiveATask && isOverAColumn) {
      setCandidates((old) => {
        if (!old) return;
        const activeIndex = old.findIndex((t) => t.id === activeId);
        old[activeIndex].state = overId as any;
        setChanges((o) => ({...o, [activeId]: {...o[activeId], state: overId as any}}));
        return arrayMove(old, activeIndex, activeIndex);
      });
    }
  }
};

export default CandidatesPage;
